import React, { useState } from 'react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Download, CheckCircle, FileText, Sparkles } from 'lucide-react';
import { trackPlausibleEvent } from '../utils/tracking';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

const LeadMagnetForm: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        trackPlausibleEvent('lead_submit', { form: 'roi_30' });
        setTimeout(() => setSubmitted(true), 500);
    };

    const benefits = [
        'Zobacz 3 realne case studies wdrożeń AI z konkretnymi liczbami.',
        'Skorzystaj z prostych wyliczeń oszczędności dla Twojej firmy.',
        'Oceń gotowość swojej firmy dzięki praktycznej checkliście.'
    ];

    return (
        <GlassCard variant="strong" className="max-w-2xl mx-auto">
            <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-primary-light dark:text-primary-dark" />
                </div>
                <h3 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                    Pobierz PDF 'Co się zwraca w 30 dni'
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    Zostaw swój email, aby otrzymać darmowy materiał i analizę.
                </p>
            </div>

            <ul className="space-y-3 mb-6">
                {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary-light dark:text-text-secondary-dark">{benefit}</span>
                    </li>
                ))}
            </ul>

            {!submitted ? (
                <form
                    id="lead-form"
                    action="https://nodessc.com/webhook/roi-lead-magnet"
                    method="POST"
                    className="space-y-4"
                    onSubmit={handleSubmit}
                    target="_blank"
                >
                    <div>
                        <label htmlFor="name-lead" className="sr-only">Twoje imię</label>
                        <input
                            type="text"
                            id="name-lead"
                            name="name"
                            required
                            placeholder="Jan"
                            className="w-full p-4 bg-white/50 dark:bg-white/5 border border-white/20 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all text-text-primary-light dark:text-text-primary-dark"
                        />
                    </div>
                    <div>
                        <label htmlFor="email-lead" className="sr-only">Twój e‑mail</label>
                        <input
                            type="email"
                            id="email-lead"
                            name="email"
                            required
                            placeholder="jan@firma.pl"
                            className="w-full p-4 bg-white/50 dark:bg-white/5 border border-white/20 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all text-text-primary-light dark:text-text-primary-dark"
                        />
                    </div>
                    {/* honeypot */}
                    <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div>
                        <label className="flex items-start gap-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <input type="checkbox" name="consent" required className="mt-0.5 accent-primary-light dark:accent-primary-dark" />
                            <span>Wyrażam zgodę na otrzymanie PDF oraz informacji o nowościach (newsletter). Twoje dane są bezpieczne i kasowane co 72h. Możesz zrezygnować w każdej chwili.</span>
                        </label>
                    </div>
                    <button
                        type="submit"
                        className="w-full px-6 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 inline-flex items-center justify-center gap-2"
                    >
                        <Download className="w-5 h-5" />
                        Pobierz PDF 'Zwrot w 30 dni'
                    </button>
                </form>
            ) : (
                <div className="text-center p-6 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-green-600 dark:text-green-400 font-semibold">
                        Sprawdź e‑mail – wysłaliśmy link do pobrania.
                    </p>
                </div>
            )}

            <div className="mt-8 pt-6 border-t border-white/10">
                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 text-center">Najczęstsze pytania</h4>
                <dl className="space-y-4">
                    <div>
                        <dt className="font-semibold text-text-primary-light dark:text-text-primary-dark">Co dokładnie dostanę?</dt>
                        <dd className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Otrzymasz PDF z analizą 3 realnych wdrożeń AI, prostymi wyliczeniami oszczędności i checklistą, która pomoże Ci ocenić gotowość Twojej firmy.</dd>
                    </div>
                    <div>
                        <dt className="font-semibold text-text-primary-light dark:text-text-primary-dark">Dla kogo jest ten materiał?</dt>
                        <dd className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Dla właścicieli małych i średnich firm, menedżerów i specjalistów, którzy chcą podejmować decyzje o inwestycji w AI na podstawie danych, a nie szumu medialnego.</dd>
                    </div>
                    <div>
                        <dt className="font-semibold text-text-primary-light dark:text-text-primary-dark">Kiedy warto wdrożyć AI?</dt>
                        <dd className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Jeśli spędzasz więcej niż 5 godzin tygodniowo na powtarzalnych zadaniach lub czujesz, że konkurencja zyskuje przewagę technologiczną – to idealny moment.</dd>
                    </div>
                </dl>
            </div>
        </GlassCard>
    );
};

export const LeadMagnet15PomyslowAiPage: React.FC = () => {
    usePageMetadata({
        title: "PDF: co się zwraca w 30 dni | Darmowy poradnik",
        description: "Odbierz darmowy PDF z 3 case studies, prostymi wyliczeniami oszczędności i checklistą gotowości Twojej firmy na wdrożenie AI.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Co się zwraca w 30 dni: darmowy poradnik",
            "author": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "publisher": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://biznesbezklikania.pl/logo.png"
                }
            },
            "datePublished": new Date().toISOString()
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="purple" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10 max-w-4xl">
                    <RevealOnScroll>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/30 dark:border-primary-dark/30 mb-6">
                            <Sparkles className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                            <span className="text-sm font-medium text-primary-light dark:text-primary-dark">Darmowy PDF</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Co się zwraca w 30 dni
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-8">
                            Podejmuj decyzje na podstawie danych, a nie szumu. Pobierz nasz przewodnik i zobacz, jak policzyć realny zwrot z inwestycji w AI.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <a
                            href="#lead-magnet-form"
                            className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-2xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center gap-2 animate-glow"
                        >
                            <Download className="w-5 h-5" />
                            Pobierz PDF teraz
                        </a>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Lead Magnet Form Section */}
            <section id="lead-magnet-form" className="py-16 md:py-20 relative scroll-mt-20">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <RevealOnScroll>
                        <LeadMagnetForm />
                    </RevealOnScroll>
                </div>
            </section>
        </>
    );
};
